import { Canvas, Point, TPointerEventInfo } from 'fabric';
import { useEffect, useRef } from 'react';
import { ActiveTool } from '../../canvas/editor-canvas-context';

/**
 * Enables canvas panning with Alt + drag or while the pan tool is active.
 * Also syncs selection behavior and cursor state with the active tool.
 */
export const useCanvasPan = (canvas: Canvas | null, activeTool: ActiveTool = 'select'): void => {
  const activeToolRef = useRef(activeTool);
  activeToolRef.current = activeTool;

  useEffect(() => {
    if (!canvas) return;
    const isPanMode = activeTool === 'pan';
    canvas.selection = !isPanMode;
    canvas.skipTargetFind = isPanMode;
    canvas.defaultCursor = isPanMode ? 'grab' : 'default';
    canvas.setCursor(canvas.defaultCursor);
  }, [canvas, activeTool]);

  useEffect(() => {
    if (!canvas) return;

    let isDragging = false;
    let lastPosX = 0;
    let lastPosY = 0;

    const handleMouseDown = (opt: TPointerEventInfo) => {
      const evt = opt.e as MouseEvent;
      if (!evt.altKey && activeToolRef.current !== 'pan') return;
      isDragging = true;
      canvas.selection = false;
      canvas.skipTargetFind = true;
      lastPosX = evt.clientX;
      lastPosY = evt.clientY;
      canvas.defaultCursor = 'grabbing';
      canvas.setCursor('grabbing');
    };

    const handleMouseMove = (opt: TPointerEventInfo<MouseEvent>) => {
      if (!isDragging) return;
      const e = opt.e;
      canvas.relativePan(new Point(e.clientX - lastPosX, e.clientY - lastPosY));
      canvas.requestRenderAll();
      lastPosX = e.clientX;
      lastPosY = e.clientY;
    };

    const handleMouseUp = () => {
      if (!isDragging) return;
      isDragging = false;
      const isPanMode = activeToolRef.current === 'pan';
      canvas.selection = !isPanMode;
      canvas.skipTargetFind = isPanMode;
      canvas.defaultCursor = isPanMode ? 'grab' : 'default';
      canvas.setCursor(canvas.defaultCursor);
    };

    canvas.on('mouse:down', handleMouseDown);
    canvas.on('mouse:move', handleMouseMove);
    canvas.on('mouse:up', handleMouseUp);

    return () => {
      canvas.off('mouse:down', handleMouseDown);
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:up', handleMouseUp);
    };
  }, [canvas]);
};
